import { Button } from "@components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@components/ui/dialog";
import useDialog from "@hooks/useDialog";
import { useMusicControl } from "@hooks/useMusicControl";
import { useScreen } from "@hooks/useScreen";
import { useScreens } from "@hooks/useScreens";
import { useQueryClient } from "@tanstack/react-query";
import { useMusicGroupQuery } from "@utils/queries";
import { supabase } from "@utils/serverClient";
import { DeleteMusicIntegrationImpl } from "./screens/DeleteMusicIntegrationButton";

interface RequestControlDialogProps {
  screenId: string;
  locationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RequestControlDialog({
  screenId,
  locationId,
  open,
  onOpenChange,
}: RequestControlDialogProps) {
  const queryClient = useQueryClient()
  const { showSuccessSnackbar, showErrorSnackbar } = useDialog()
  const { requestControl } = useMusicControl(screenId, locationId);
  const removeMutation = useMusicGroupQuery.removeFromGroup()
  const { screenInfo } = useScreen()
  const { screens, updateScreen } = useScreens()

  const handleRequest = async () => {
    requestControl.mutate(undefined, {
      onSuccess() {
        showSuccessSnackbar("Control request sent successfully");
        onOpenChange(false);
      },
      onError(error) {
        showErrorSnackbar("Failed to send control request");
        console.error(error);
      },
    });
  };

  const handleUnsync = async () => {
    if (!screenInfo?.group_id) {
      return showErrorSnackbar("No group associated with this screen")
    }
    try {
      removeMutation.mutate({
        groupId: screenInfo.group_id,
        screenWithSameGroupCount: screens.filter(s => s.group_id === screenInfo.group_id).length,
      }, {
        async onSuccess() {
          // Delete music control record for the screen
          await supabase
            .from('music_control')
            .delete()
            .eq('screen_id', screenInfo.id ?? "")
            .eq('location_id', screenInfo.locationId ?? "");

          updateScreen({
            screenId: screenInfo.id ?? "",
            data: {
              group_id: null,
            }
          })
          showSuccessSnackbar("Successfully unsynced music from location")

          // Invalidate music control queries
          queryClient.invalidateQueries({ queryKey: ['music-control'] })
        },
        onError(error) {
          showErrorSnackbar("Failed to unsync music from location")
          console.error(error)
        }
      })
      showSuccessSnackbar("Successfully unsynced from location music");
      onOpenChange(false);
    } catch (error) {
      showErrorSnackbar("Failed to unsync from location music");
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Music Control</DialogTitle>
          <DialogDescription>
            You need to request control from the current owner to make changes to the music settings.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-row gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleUnsync}
                disabled={removeMutation.isPending}
              >
                {removeMutation.isPending ? "Unsyncing..." : "Unsync"}
              </Button>
              <DeleteMusicIntegrationImpl size="sm">
                {/* Delete Music Integration */}
              </DeleteMusicIntegrationImpl>
            </div>
            <div className="flex flex-row gap-2 justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleRequest} disabled={requestControl.isPending}>
                {requestControl.isPending ? "Requesting..." : "Request Control"}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}